import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Badge, Empty, AgentJsonBox, statusTone } from "@/components/admin/Ui";
import { Handshake, Globe, Megaphone, Sparkle, PaperPlaneTilt, Copy, WhatsappLogo, Warning, Shield } from "@phosphor-icons/react";

export default function Members() {
  const [members, setMembers] = useState([]);
  const [matches, setMatches] = useState(null);
  const [busy, setBusy] = useState(false);
  const [announcement, setAnnouncement] = useState(null);
  const [invitePayload, setInvitePayload] = useState(null);
  const [waPayload, setWaPayload] = useState(null);

  const load = async () => {
    const { data } = await api.get("/members");
    setMembers(data.members || []);
  };
  useEffect(() => { load(); }, []);

  const runMatch = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/members/match", { max_matches: 5 });
      setMatches(data);
      toast.success(`${(data.matches || []).length} matches suggested`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Match failed"); }
    finally { setBusy(false); }
  };

  const announce = async (member, kind) => {
    setAnnouncement({ member, kind, loading: true, content: null });
    try {
      const topic = kind === "welcome"
        ? `Welcome our newest member ${member.company_name} (${member.city ? member.city + ", " : ""}${member.country}) — ${member.about || member.trade_lanes || member.services?.join(", ") || "network expansion"}.`
        : `Celebrating ${member.company_name}'s ${kind.replace("renewal_", "")} anniversary as an EPAS member — earned respect, no volume brags.`;
      const { data } = await api.post("/content/generate", {
        content_type: kind === "welcome" ? "member_spotlight" : "linkedin_post",
        topic,
        additional_context: `Member: ${JSON.stringify(member)}. Kind: ${kind}.`,
      });
      setAnnouncement({ member, kind, loading: false, content: data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft failed");
      setAnnouncement(null);
    }
  };

  const sendInvite = async (member) => {
    try {
      const { data } = await api.post(`/members/${member.id}/invite`);
      setInvitePayload({ member, ...data });
      toast.success("Portal invite generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    }
  };

  const openWhatsApp = async (member) => {
    try {
      const { data } = await api.get(`/whatsapp/members/${member.id}/welcome`);
      setWaPayload({ member, ...data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "WhatsApp lookup failed");
    }
  };

  const markInvited = async (member) => {
    try {
      await api.post(`/whatsapp/members/${member.id}/mark-invited`);
      toast.success("Marked as WhatsApp-invited");
      setWaPayload(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark");
    }
  };

  return (
    <div data-testid="admin-members-page">
      <PageHeader title="Members" subtitle="Agent 7 · GPT-5" action={
        <button data-testid="members-match-button" onClick={runMatch} disabled={busy} className="btn-primary">
          <Handshake size={14} /> {busy ? "Matching…" : "Run Matching Agent"}
        </button>
      } />
      <div className="p-8 space-y-6">
        {busy && !matches && (
          <div className="bg-white border border-black/10 p-5 flex items-center gap-3 text-sm text-navy/70" data-testid="members-match-loading">
            <div className="w-3 h-3 rounded-full bg-ember animate-pulse" />
            Matching agent is reviewing trade lanes and services… this can take 30–60 seconds.
          </div>
        )}
        {members.length === 0 ? <Empty label="No members yet." /> : (
          <div className="bg-white border border-black/10 overflow-x-auto">
            <table className="w-full text-sm row-alt" data-testid="members-table">
              <thead className="border-b border-black/10 text-left text-navy/60">
                <tr className="text-[10px] uppercase tracking-widest">
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">Territory</th>
                  <th className="px-4 py-3">Services</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Renewal</th>
                  <th className="px-4 py-3 text-right">Portal</th>
                  <th className="px-4 py-3 text-right">Broadcast</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-b border-black/5" data-testid={`member-row-${m.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-navy">{m.company_name}</div>
                      <div className="text-[11px] text-navy/50">{m.city}, {m.country}</div>
                    </td>
                    <td className="px-4 py-3 text-navy/80">{m.territory || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(m.services || []).map((s) => (
                          <span key={s} className="text-[10px] uppercase tracking-widest border border-navy/20 px-1.5 py-0.5 text-navy/70">{s}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-navy/70">{m.membership_type?.replace("_", " ") || "—"}</td>
                    <td className="px-4 py-3"><Badge tone={statusTone(m.status)}>{m.status}</Badge></td>
                    <td className="px-4 py-3 text-xs text-navy/60">{m.renewal_date || "—"}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => sendInvite(m)} disabled={!m.contact_email} data-testid={`member-invite-${m.id}`} className="text-xs uppercase tracking-widest text-navy hover:text-ember disabled:opacity-40 disabled:cursor-not-allowed mr-3">
                        <PaperPlaneTilt size={12} className="inline" /> Invite
                      </button>
                      <button onClick={() => openWhatsApp(m)} data-testid={`member-whatsapp-${m.id}`}
                        className={`text-xs uppercase tracking-widest hover:text-emerald-700 ${m.whatsapp_invited_at ? "text-emerald-600" : "text-navy"}`}
                        title={m.whatsapp_invited_at ? `Invited ${new Date(m.whatsapp_invited_at).toLocaleDateString()}` : "Compose WhatsApp welcome"}>
                        <WhatsappLogo size={12} weight={m.whatsapp_invited_at ? "fill" : "regular"} className="inline" /> WhatsApp
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => announce(m, "welcome")} data-testid={`member-welcome-${m.id}`} className="text-xs uppercase tracking-widest text-navy hover:text-ember mr-3">
                        <Sparkle size={12} className="inline" /> Welcome
                      </button>
                      <button onClick={() => announce(m, "renewal_1yr")} data-testid={`member-renewal-${m.id}`} className="text-xs uppercase tracking-widest text-navy hover:text-ember">
                        <Megaphone size={12} className="inline" /> Renewal
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {matches && (
          <div className="bg-white border border-black/10 p-6" data-testid="members-matches-panel">
            <div className="flex items-center gap-2 mb-4">
              <Globe size={16} weight="duotone" className="text-ember" />
              <div className="overline">Suggested introductions</div>
            </div>
            <div className="space-y-4">
              {(matches.matches || []).map((m, i) => (
                <div key={i} className="border border-black/10 p-4" data-testid={`match-item-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-navy">{m.member_a} <span className="text-navy/40 mx-2">→</span> {m.member_b}</div>
                    <span className="ship-number text-lg font-bold text-ember">{m.confidence_score}/10</span>
                  </div>
                  <div className="text-sm text-navy/70 mt-2">{m.reason_for_match}</div>
                  {m.suggested_intro_email && (
                    <div className="mt-3 bg-navy/5 border border-navy/10 p-3 text-sm text-navy whitespace-pre-wrap">{m.suggested_intro_email}</div>
                  )}
                </div>
              ))}
              {(!matches.matches || matches.matches.length === 0) && (
                <AgentJsonBox data={matches} testId="matches-raw-output" />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Portal invite result modal */}
      {invitePayload && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm" data-testid="member-invite-modal" onClick={() => setInvitePayload(null)}>
          <div className="bg-white w-full max-w-xl border border-black/10 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-black/10 flex items-center justify-between">
              <div>
                <div className="overline">Portal invite generated</div>
                <div className="font-display font-bold text-navy mt-1">{invitePayload.member?.company_name}</div>
              </div>
              <button onClick={() => setInvitePayload(null)} className="text-navy/50 hover:text-navy text-2xl leading-none">×</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-sm text-navy/80">
                Send this one-time link to <span className="font-mono">{invitePayload.email}</span>. It expires in 72 hours
                and can be used only once. EPAS never emails plaintext passwords.
              </div>
              <div>
                <div className="overline mb-1">Invite URL</div>
                <textarea
                  readOnly
                  value={invitePayload.invite_url}
                  data-testid="member-invite-url"
                  className="w-full border border-black/15 px-3 py-2 text-xs font-mono h-24"
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => { navigator.clipboard.writeText(invitePayload.invite_url); toast.success("Invite URL copied"); }}
                  data-testid="member-invite-copy"
                  className="btn-primary"
                >
                  <Copy size={14} /> Copy invite URL
                </button>
                <button onClick={() => setInvitePayload(null)} className="btn-secondary">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Announcement preview modal */}
      {announcement && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm" data-testid="member-announcement-modal">
          <div className="bg-white w-full max-w-2xl max-h-[85vh] overflow-y-auto border border-black/10 shadow-2xl">
            <div className="px-6 py-4 border-b border-black/10 flex items-center justify-between">
              <div>
                <div className="overline">{announcement.kind === "welcome" ? "Welcome announcement" : `Anniversary · ${announcement.kind}`}</div>
                <div className="font-display font-bold text-navy mt-1">{announcement.member?.company_name}</div>
              </div>
              <button onClick={() => setAnnouncement(null)} className="text-navy/50 hover:text-navy text-2xl leading-none">×</button>
            </div>
            {announcement.loading ? (
              <div className="p-10 text-center text-navy/60">Drafting broadcast — GPT-5-mini is composing…</div>
            ) : (
              <div className="p-6 space-y-4">
                {announcement.content?.title && (
                  <div><div className="overline mb-1">Title</div><div className="font-display font-bold text-lg text-navy" data-testid="announcement-title">{announcement.content.title}</div></div>
                )}
                <div>
                  <div className="overline mb-1">Content</div>
                  <textarea readOnly value={announcement.content?.content || ""} className="w-full border border-black/15 px-3 py-2 text-sm h-64 font-mono" data-testid="announcement-content" />
                </div>
                {announcement.content?.hashtags?.length > 0 && (
                  <div className="text-xs text-ember">{announcement.content.hashtags.map((h) => `#${h}`).join(" ")}</div>
                )}
                <div className="flex items-center gap-3 pt-2">
                  <button
                    onClick={() => { navigator.clipboard.writeText(`${announcement.content?.title}\n\n${announcement.content?.content}\n\n${(announcement.content?.hashtags||[]).map((h)=>`#${h}`).join(" ")}`); toast.success("Copied — paste into LinkedIn"); }}
                    data-testid="announcement-copy-button"
                    className="btn-primary"
                  >Copy for LinkedIn</button>
                  <button onClick={() => setAnnouncement(null)} className="btn-secondary">Close</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* WhatsApp welcome compose modal */}
      {waPayload && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm" data-testid="member-whatsapp-modal" onClick={() => setWaPayload(null)}>
          <div className="bg-white w-full max-w-2xl border border-black/10 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-black/10 flex items-center justify-between">
              <div>
                <div className="overline flex items-center gap-2"><WhatsappLogo size={12} weight="fill" className="text-emerald-600" /> WhatsApp welcome DM</div>
                <div className="font-display font-bold text-navy mt-1">{waPayload.member?.company_name}</div>
              </div>
              <button onClick={() => setWaPayload(null)} className="text-navy/50 hover:text-navy text-2xl leading-none">×</button>
            </div>
            <div className="p-6 space-y-4">
              {(waPayload.warnings || []).length > 0 && (
                <div className="border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="wa-warnings">
                  <div className="font-semibold mb-1 flex items-center gap-1"><Warning size={12} weight="fill" /> Before you send</div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {waPayload.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
              <div>
                <div className="overline mb-1">Recipient number</div>
                {waPayload.phone ? (
                  <div className="border border-black/15 px-3 py-2 text-sm font-mono" data-testid="wa-phone">+{waPayload.phone}</div>
                ) : (
                  <div className="flex gap-2" data-testid="wa-phone-add">
                    <input
                      type="tel"
                      placeholder="+38640793000"
                      className="flex-1 border border-black/15 px-3 py-2 text-sm"
                      data-testid="wa-phone-input"
                      onKeyDown={async (e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.currentTarget.blur();
                        }
                      }}
                    />
                    <button
                      onClick={async (e) => {
                        const input = e.currentTarget.parentElement.querySelector('input');
                        const num = input.value.trim();
                        if (!num) { toast.error("Enter a phone number first"); return; }
                        try {
                          await api.patch(`/members/${waPayload.member.id}`, { whatsapp_number: num });
                          toast.success("Number saved");
                          const { data } = await api.get(`/whatsapp/members/${waPayload.member.id}/welcome`);
                          setWaPayload({ member: waPayload.member, ...data });
                          load();
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || "Save failed");
                        }
                      }}
                      data-testid="wa-phone-save"
                      className="bg-navy text-white px-4 py-2 text-xs uppercase tracking-widest hover:bg-navy/90"
                    >Save</button>
                  </div>
                )}
              </div>
              <div>
                <div className="overline mb-1">Message (playbook-clean, ready to send)</div>
                <textarea readOnly value={waPayload.message} data-testid="wa-message"
                  className="w-full border border-black/15 px-3 py-2 text-sm h-56 font-sans" />
              </div>
              {waPayload.guard && !waPayload.guard.ok && (
                <div className="border border-red-200 bg-red-50 p-3 text-xs text-red-800" data-testid="wa-guard-fail">
                  <div className="font-semibold mb-1 flex items-center gap-1"><Warning size={12} weight="fill" /> Guard flagged {waPayload.guard.violations.length} issue(s)</div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {waPayload.guard.violations.map((v, i) => <li key={i}><span className="font-medium">{v.rule}</span> — {v.hint}</li>)}
                  </ul>
                </div>
              )}
              {waPayload.guard?.ok && (
                <div className="border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-800 flex items-center gap-2" data-testid="wa-guard-ok">
                  <Shield size={12} /> Guard passed — safe to send.
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <a href={waPayload.wa_link || "#"}
                  target="_blank" rel="noopener noreferrer"
                  onClick={(e) => { if (!waPayload.wa_link) { e.preventDefault(); toast.error("Add a WhatsApp number to the member first."); } }}
                  data-testid="wa-open-link"
                  className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-widest px-4 py-2 ${waPayload.wa_link ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-black/10 text-navy/40 cursor-not-allowed"}`}>
                  <WhatsappLogo size={14} weight="fill" /> Open in WhatsApp
                </a>
                <button
                  onClick={async () => {
                    if (!waPayload.phone) { toast.error("Add a WhatsApp number first."); return; }
                    if (!waPayload.guard?.ok) { toast.error("Guard failed — fix violations before sending."); return; }
                    if (!window.confirm(`Send this DM to +${waPayload.phone} via Twilio right now?`)) return;
                    try {
                      const { data } = await api.post(`/whatsapp/members/${waPayload.member.id}/send-twilio`);
                      toast.success(`Sent via Twilio · sid ${data.twilio.sid}`);
                      setWaPayload(null); load();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Twilio send failed");
                    }
                  }}
                  data-testid="wa-send-twilio"
                  className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest bg-navy text-white px-4 py-2 hover:bg-navy/90 disabled:opacity-40"
                  disabled={!waPayload.phone || !waPayload.guard?.ok}
                >
                  <WhatsappLogo size={14} weight="fill" /> Send via Twilio
                </button>
                <button onClick={() => { navigator.clipboard.writeText(waPayload.message); toast.success("Message copied"); }} data-testid="wa-copy-message" className="btn-secondary">
                  <Copy size={14} /> Copy message
                </button>
                <button onClick={() => markInvited(waPayload.member)} data-testid="wa-mark-invited" className="text-xs uppercase tracking-widest text-navy/70 hover:text-navy ml-auto">
                  Mark as invited
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
